import axios from 'axios';
import { useAuthStore } from '../stores/auth.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import { devLog, devWarn, devError } from './dev-logger';

let lastResponseRequestId: string | null = null;

export function getLastResponseRequestId(): string | null {
  return lastResponseRequestId;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple logout calls
let isLoggingOut = false;
let refreshPromise: Promise<RefreshPayload> | null = null;
const CSRF_COOKIE_NAME = 'pm_csrf_token';

export type RefreshPayload = {
  accessToken?: string | null;
  refreshToken?: string | null;
  csrfToken?: string | null;
  user?: unknown;
};

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * No browser, o cookie `pm_csrf_token` (compartilhado entre subdomínios quando a API
 * usa AUTH_COOKIE_DOMAIN) é a fonte de verdade após reload; o store pode ficar stale.
 */
function getCsrfToken(): string | null {
  if (typeof document !== 'undefined') {
    const fromCookie = getCookieValue(CSRF_COOKIE_NAME);
    if (fromCookie) {
      return fromCookie;
    }
  }
  return useAuthStore.getState().csrfToken;
}

async function performRefresh(): Promise<RefreshPayload> {
  const csrfToken = getCsrfToken();
  const refreshResponse = await axios.post(
    `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'}/auth/refresh`,
    {},
    {
      withCredentials: true,
      headers: csrfToken ? { 'x-csrf-token': csrfToken } : undefined,
    },
  );

  return (refreshResponse.data?.data || refreshResponse.data) as RefreshPayload;
}

async function getRefreshPayload(): Promise<RefreshPayload> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

/**
 * Refresh deduplicado (mesma promise que o interceptor de 401).
 * Use no bootstrap após reload para evitar corridas com outras renovações.
 */
export async function refreshAuthSession(): Promise<RefreshPayload> {
  return getRefreshPayload();
}

// Function to handle logout and redirect
const handleUnauthorized = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Clear stores using getState() to access without hooks
  const authStore = useAuthStore.getState();
  const workspaceStore = useWorkspaceStore.getState();
  
  // Call logout methods to clean up stores
  authStore.logout();
  workspaceStore.clearWorkspace();

  // Clear all storage (redundant but ensures cleanup)
  localStorage.removeItem('workspaceId');
  
  // Clear zustand persisted storage
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('workspace-storage');

  // Clear session storage as well
  sessionStorage.clear();

  // Reset flag after a short delay
  setTimeout(() => {
    isLoggingOut = false;
  }, 1000);

  // Redirect to login
  const isLoginPage = window.location.pathname === '/login';
  if (!isLoginPage) {
    window.location.href = '/login';
  }
};

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
  }

  // GET /workspaces é endpoint global, não deve receber contexto de workspace.
  const normalizedUrl = config.url?.split('?')[0] ?? '';
  const isListWorkspacesRequest =
    config.method?.toLowerCase() === 'get' &&
    (normalizedUrl === 'workspaces' || normalizedUrl === '/workspaces');
  const isPermissionsMeRequest =
    normalizedUrl === 'permissions/me' || normalizedUrl === '/permissions/me';

  if (!config.headers['X-Workspace-Id'] && !isListWorkspacesRequest) {
    let fromUrl: string | null = null;
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/w/')) {
      const match = window.location.pathname.match(/^\/w\/([a-f0-9-]+)/i);
      if (match) fromUrl = match[1];
    }
    const fromStore = useWorkspaceStore.getState().currentWorkspace?.id;
    const workspaceId = fromUrl || fromStore;
    if (workspaceId) {
      config.headers['X-Workspace-Id'] = workspaceId;
    }
  }

  if (isPermissionsMeRequest) {
    const headerWorkspaceId = config.headers['X-Workspace-Id'];
    if (!headerWorkspaceId) {
      devWarn('permissions', '/permissions/me sem X-Workspace-Id', {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        method: config.method,
        url: config.url,
      });
    } else {
      devLog('permissions', '/permissions/me com contexto de workspace', {
        workspaceId: headerWorkspaceId,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    }
  }

  // Don't set Content-Type for FormData - let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    const requestId =
      response.headers?.['x-request-id'] || response.headers?.['X-Request-Id'];
    if (typeof requestId === 'string' && requestId.length > 0) {
      lastResponseRequestId = requestId;
    }
    return response;
  },
  async (error) => {
    if (error.config?.url?.includes('/permissions/me')) {
      devWarn('permissions', 'Falha ao carregar /permissions/me', {
        status: error.response?.status,
        message: error.response?.data?.message,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    }

    // Limite do plano: abre LimitExceededModal (evita alert nativo nos callers)
    if (typeof window !== 'undefined') {
      const { tryHandleLimitExceededFromApiError } = await import('../hooks/useLimitExceeded');
      tryHandleLimitExceededFromApiError(error);
    }

    // Handle 403 workspace blocked / expired (show modal, then reject)
    if (error.response?.status === 403) {
      const data = error.response?.data;
      const code = data?.code;
      const workspaceId = data?.workspaceId;
      if (
        (code === 'WORKSPACE_BLOCKED' || code === 'WORKSPACE_EXPIRED') &&
        typeof workspaceId === 'string'
      ) {
        const { useWorkspaceBlockedStore } = await import('../stores/workspaceBlockedStore');
        useWorkspaceBlockedStore.getState().show({
          code,
          workspaceId,
          workspaceName: data?.workspaceName,
        });
      }
    }

    if (error.response?.status === 401) {
      // Don't redirect if already on login page or if it's a login/refresh request
      const isLoginPage = window.location.pathname === '/login';
      const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                           error.config?.url?.includes('/auth/refresh');

      if (isLoginPage || isAuthRequest) {
        return Promise.reject(error);
      }

      const originalConfig = error.config as any;
      if (originalConfig?._retry) {
        handleUnauthorized();
        return Promise.reject(error);
      }

      originalConfig._retry = true;

      try {
        const payload = await getRefreshPayload();
        const authStore = useAuthStore.getState();
        if (payload?.user) {
          authStore.setAuth(
            payload.user as any,
            payload?.accessToken ?? null,
            payload?.refreshToken ?? null,
            payload?.csrfToken ?? null,
          );
        } else {
          authStore.setSession(
            payload?.accessToken ?? null,
            payload?.refreshToken ?? null,
            payload?.csrfToken ?? null,
          );
        }

        if (payload?.accessToken) {
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.Authorization = `Bearer ${payload.accessToken}`;
        }

        return api.request(originalConfig);
      } catch (refreshError) {
        devError('auth', 'Falha no refresh token', refreshError);
        handleUnauthorized();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
